<template>
  <div
    class="cf-card cf-flex cf-clear-padding cf-max-width-500"
  >
    <!-- Challenge Image or Progress Ring Container -->
    <div class="cf-flex cf-flex--align-center cf-flex--justify-center cf-padding-14 cf-flex--no-shrink">
      <img
        v-if="challengeImage"
        class="cf-image cf-image--thumbnail"
        :src="challengeImage"
        :alt="challengeName"
        onerror="this.src='/images/image-not-found.png';"
      >
      <cf-progress-ring
        v-else
        :progress="currentBalance"
        :steps="finalTarget"
      />
    </div>

    <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-padding-y-16 cf-padding-right-16 cf-full-width">
      <!-- Days Remaining Display -->
      <div
        v-if="daysRemainingText"
        class="cf-flex cf-flex--row cf-flex--justify-start cf-flex--align-center cf-flex--gap-8"
      >
        <div class="cf-icon cf-icon--sm far fa-clock cf-color-primary" />
        <p class="cf-subtitle cf-subtitle--2 cf-color-primary bold">
          {{ daysRemainingText }}
        </p>
      </div>

      <!-- Challenge Name -->
      <v-clamp
        tag="h4"
        class="cf-title cf-title--4"
        autoresize
        :max-lines="2"
      >
        {{ challengeName }}
      </v-clamp>

      <!-- Challenge Description -->
      <v-clamp
        v-if="challengeDescription"
        tag="p"
        class="cf-subtitle cf-subtitle--2"
        autoresize
        :max-lines="2"
      >
        {{ challengeDescription }}
      </v-clamp>

      <!-- Progress Bar -->
      <cf-progress-bar
        :current-value="currentBalance"
        :max-value="finalTarget"
        :min-value="0"
        :thresholds="progressBarThresholds"
        :format-value="formatThresholdAmount"
        show-start-end-labels
      />

      <!-- Reward Status Messages -->
      <div
        v-if="rewardStatusMessage"
        class="cf-reward-status cf-color-primary"
      >
        <p class="cf-caption cf-caption--bold">{{ rewardStatusMessage }}</p>
      </div>

      <!-- Learn More Button -->
      <div class="cf-flex cf-flex--gap-12 cf-flex--align-center cf-flex--justify-end">
        <p
          class="cf-link cf-link--heavy"
          @click="learnMoreClickHandler"
        >
          {{ learnMoreText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  import CfProgressRing from '@/view/components/shared/cf-progress-ring/index.vue';
  import CfProgressBar from '@/view/components/shared/cf-progress-bar/index.vue';

  export default {
    name: 'CfChallengeCard',

    components: {
      CfProgressRing,
      CfProgressBar
    },

    props: {
      challenge: {
        type: Object,
        required: true
      }
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),

      challengeName() {
        return this.challenge.currency?.title || '';
      },

      challengeDescription() {
        return this.challenge.currency?.description || '';
      },

      challengeImage() {
        // Note: challengeSummaryCardImagePath will be available when unblocked
        return this.challenge.currency?.defaultImagePath || null;
      },

      challengeEndDate() {
        return this.challenge.challengeEndDate;
      },

      currentBalance() {
        return this.challenge.currentBalance || 0;
      },

      isSpendChallenge() {
        return this.challenge.currency?.accumulationType === 'Spend';
      },

      isMultiTieredChallenge() {
        return this.challenge.isMultiTieredChallenge;
      },

      finalTarget() {
        return this.challenge.finalChallengeThreshold?.cost || 0;
      },

      nextThreshold() {
        return this.challenge.nextChallengeThreshold;
      },


      progressBarThresholds() {
        if (!this.challenge.allChallengeThresholds) {
          return [];
        }

        // Transform challenge thresholds to generic progress bar format
        // Works for both single and multi-tiered challenges
        return [...this.challenge.allChallengeThresholds]
          .sort((a, b) => a.cost - b.cost)
          .map(threshold => ({
            id: threshold.publicId,
            value: threshold.cost,
            isCompleted: this.currentBalance >= threshold.cost
          }));
      },

      daysRemaining() {
        if (!this.challenge.challengeEndDate) return null;

        const endDate = new Date(this.challenge.challengeEndDate);
        const now = new Date();

        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const diffTime = endDateOnly - nowDateOnly;
        const daysBetween = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        const daysRemaining = daysBetween + 2;
        return Math.max(0, daysRemaining);
      },

      daysRemainingText() {
        if (!this.daysRemaining) return '';

        // Business logic based on requirements:
        // - If daysRemaining > 1: show "X days remaining"
        // - If daysRemaining <= 1 (with special case): show "ends today"
        if (this.daysRemaining > 1) {
          return `${this.daysRemaining} ${this.content?.Challenges?.metadata?.daysRemaining || 'Days Remaining'}`;
        }
        return this.content?.Challenges?.metadata?.endsToday || 'Ends Today';
      },

      rewardStatusMessage() {
        const hasUnlockedRewards = this.challenge.unlockedChallengeThresholds?.length > 0;
        const isAutomatic = this.challenge.challengeProcessingType === 'AutomaticAwardChallenge';
        const nextConversionDate = this.challenge.nextScheduledConversionDateTimeOffset;

        if (!hasUnlockedRewards) {
          return '';
        }
        if (isAutomatic) {
          return this.content?.Challenges?.metadata?.automaticReward || 'Challenge completed!';
        }
        if (nextConversionDate) {
          const date = new Date(nextConversionDate).toLocaleDateString();
          const template = this.content?.Challenges?.metadata?.unlockedReward || 'You\'ll receive your award by {{date}}';
          return template.replace('{{date}}', date);
        }

        return 'Your reward will be processed soon.';
      },

      learnMoreText() {
        return this.content?.Challenges?.metadata?.learnMore || 'Learn More';
      }
    },

    methods: {
      learnMoreClickHandler() {
        const modalPayload = {
          challenge: this.challenge,
          challengeName: this.challengeName,
          challengeDescription: this.challengeDescription
        };

        this.$openModal('challengeDetails', modalPayload);
      },

      formatThresholdAmount(amount) {
        return this.isSpendChallenge
          ? `$${Math.floor(amount)}`
          : `${amount}`;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .cf-reward-status {
    padding: 8px 12px;
    background-color: var(--color-primary-light);
    border-radius: 4px;
    border-left: 4px solid var(--color-primary);
  }
</style>
